/**
 * Mothership progression — the 5 levels (Mechanic: Mothership Progression).
 * Each level unlocks a capability band. Level 1 is fully specified here; higher
 * levels declare their unlock flags so systems can gate features off a single
 * source of truth (`capabilitiesFor`).
 */
import type { ResourceBag } from "@/game/economy/resources";

export type MothershipTier = 1 | 2 | 3 | 4 | 5;

export interface MothershipCapabilities {
  asteroidMining: boolean; // L1+
  miningDrones: boolean; // L1+
  hangarBay: boolean; // L2+  (standard fighters)
  heavyCarrier: boolean; // L3+  (bombers + interceptors)
  onboardTechnicians: boolean; // L3+  (idle repair)
  fleetRouting: boolean; // L4+  (advanced routing + captain reserve)
  superLaser: boolean; // L5    (planet obliteration)
}

export interface MothershipLevel {
  tier: MothershipTier;
  name: string; // codename
  maxHp: number;
  /** Max simultaneous mining drones this hull supports. */
  droneBays: number;
  /** Max subordinate swarm ships deployable (0 until the first hangar at L2). */
  hangarCapacity: number;
  /** Max technician tier for idle repair (0 until L3). */
  technicianMaxTier: number;
  capabilities: MothershipCapabilities;
  /** Cost to UPGRADE INTO this tier from the previous one. */
  upgradeCost: Partial<ResourceBag>;
  blurb: string;
}

export const MOTHERSHIP_LEVELS: Record<MothershipTier, MothershipLevel> = {
  1: {
    tier: 1,
    name: "The Prospector",
    maxHp: 1000,
    droneBays: 4,
    hangarCapacity: 0,
    technicianMaxTier: 0,
    capabilities: caps({ asteroidMining: true, miningDrones: true }),
    upgradeCost: {}, // starting hull
    blurb: "Basic asteroid mining and automated mining drones.",
  },
  2: {
    tier: 2,
    name: "The Escort Carrier",
    maxHp: 2600,
    droneBays: 8,
    hangarCapacity: 6,
    technicianMaxTier: 0,
    capabilities: caps({
      asteroidMining: true,
      miningDrones: true,
      hangarBay: true,
    }),
    upgradeCost: { minerals: 5000 },
    blurb: "First hangar bay; deploy a small wing of standard fighters.",
  },
  3: {
    tier: 3,
    name: "The Swarm Host",
    maxHp: 6000,
    droneBays: 12,
    hangarCapacity: 24,
    technicianMaxTier: 3,
    capabilities: caps({
      asteroidMining: true,
      miningDrones: true,
      hangarBay: true,
      heavyCarrier: true,
      onboardTechnicians: true,
    }),
    upgradeCost: { minerals: 22000, alloy: 1200 },
    blurb: "True carrier: heavy bombers + interceptors, plus on-board technicians.",
  },
  4: {
    tier: 4,
    name: "Armada Command",
    maxHp: 14000,
    droneBays: 16,
    hangarCapacity: 60,
    technicianMaxTier: 6,
    capabilities: caps({
      asteroidMining: true,
      miningDrones: true,
      hangarBay: true,
      heavyCarrier: true,
      onboardTechnicians: true,
      fleetRouting: true,
    }),
    upgradeCost: { minerals: 90000, alloy: 8000, plasma: 1500 },
    blurb: "Advanced fleet routing; prepares the captain reserve roster.",
  },
  5: {
    tier: 5,
    name: "The World Breaker",
    maxHp: 32000,
    droneBays: 24,
    hangarCapacity: 120,
    technicianMaxTier: 10,
    capabilities: caps({
      asteroidMining: true,
      miningDrones: true,
      hangarBay: true,
      heavyCarrier: true,
      onboardTechnicians: true,
      fleetRouting: true,
      superLaser: true,
    }),
    upgradeCost: { minerals: 400000, alloy: 45000, plasma: 12000 },
    blurb: "Super-laser: obliterate planets for instant core-mineral extraction.",
  },
};

export function capabilitiesFor(tier: MothershipTier): MothershipCapabilities {
  return MOTHERSHIP_LEVELS[tier].capabilities;
}

/** Fill a capabilities record, defaulting everything unspecified to false. */
function caps(partial: Partial<MothershipCapabilities>): MothershipCapabilities {
  return {
    asteroidMining: false,
    miningDrones: false,
    hangarBay: false,
    heavyCarrier: false,
    onboardTechnicians: false,
    fleetRouting: false,
    superLaser: false,
    ...partial,
  };
}
